'use client';

import { useCallback, useEffect } from 'react';
import Link from 'next/link';
import { useTranslation } from '@/core/i18n';
import { logger } from '@/core/lib/logger';

export default function RootError({ error, reset }: { error: Error; reset: () => void }) {
  const { t } = useTranslation();

  useEffect(() => {
    logger.error('Route error boundary caught error', {
      component: 'RootError',
      stack: error.stack,
    });
  }, [error]);

  const handleRetry = useCallback(() => {
    reset();
  }, [reset]);

  return (
    <div id="root-error-page" className="min-h-screen bg-hms-bg flex items-center justify-center px-4">
      <div className="text-center max-w-md">
        <div className="w-16 h-16 rounded-full bg-hms-red/10 border-2 border-hms-red/20 flex items-center justify-center mx-auto mb-6">
          <svg className="w-8 h-8 text-hms-red" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z" />
            <line x1="12" y1="9" x2="12" y2="13" />
            <line x1="12" y1="17" x2="12.01" y2="17" />
          </svg>
        </div>
        <h1 className="text-xl font-bold text-hms-text mb-2">{t('errorPage.title')}</h1>
        <p className="text-sm text-hms-textDim mb-6">
          {error.message || t('errorPage.message')}
        </p>
        <div className="flex items-center justify-center gap-3">
          <button id="root-error-retry" type="button" onClick={handleRetry}
            className="px-5 py-2.5 rounded-lg bg-hms-gold text-black text-sm font-bold hover:bg-hms-goldLt transition-colors">
            {t('errorPage.tryAgain')}
          </button>
          <Link id="root-error-home" href="/"
            className="px-5 py-2.5 rounded-lg border border-hms-border text-sm font-semibold text-hms-textSub hover:border-hms-borderLt hover:text-hms-text transition-colors">
            {t('errorPage.goHome')}
          </Link>
        </div>
      </div>
    </div>
  );
}
